import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth, authFetch } from "../../App";
import { LayoutDashboard, FolderOpen, FileText, BookOpen, LogOut, ShieldCheck, AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const API = "/api";

/**
 * HpsLayout — eigenständiges Layout für den HPS-Partnerbereich.
 * Bewusst getrennt vom TGAone-ERP-Layout: andere Navigation, andere Farben,
 * KEIN Zugriff auf interne ERP-Inhalte.
 *
 * Beim ersten Login wird ein Passwortwechsel-Dialog angezeigt
 * (``user.must_change_password === true``).
 */
export default function HpsLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showPwModal, setShowPwModal] = useState(false);

  useEffect(() => {
    if (user?.must_change_password) {
      setShowPwModal(true);
    }
  }, [user]);

  // Sicherheit: Nicht-HPS-User raus
  useEffect(() => {
    if (user && user.role !== "partner_hps" && user.role !== "admin") {
      toast.error("Kein Zugriff auf den HPS-Bereich");
      navigate("/app", { replace: true });
    }
  }, [user, navigate]);

  const nav = [
    { to: "/app/hps-anlagencheck", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/app/hps-anlagencheck/objekte", label: "Objekte & Prüfungen", icon: FolderOpen },
    { to: "/app/hps-anlagencheck/berichte", label: "Berichte", icon: FileText },
    { to: "/app/hps-anlagencheck/kataloge", label: "Kataloge", icon: BookOpen },
  ];

  return (
    <div className="min-h-screen text-slate-100" style={{ background: "linear-gradient(135deg, #0a1628 0%, #122947 50%, #0a1628 100%)" }}>
      {/* Topbar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl border-b border-white/10" style={{ background: "rgba(10,22,40,0.85)" }}>
        <div className="px-4 md:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg,#4a9eff,#2563eb)" }}>
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold leading-tight">HPS Anlagencheck</div>
              <div className="text-[11px] text-white/50 leading-tight truncate">Geschützter Partnerbereich · Pilotversion</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:block text-right">
              <div className="text-xs text-white/80 leading-tight">{user?.name || user?.email}</div>
              <div className="text-[10px] text-white/40 leading-tight">{user?.hps_role || user?.role}</div>
            </div>
            <button onClick={() => { logout(); navigate("/partnerlogin"); }}
                    className="rounded-lg px-3 py-1.5 text-xs font-medium bg-white/5 hover:bg-white/10 border border-white/10 inline-flex items-center gap-1.5 transition-colors"
                    data-testid="hps-logout-btn">
              <LogOut className="w-3.5 h-3.5" /> Abmelden
            </button>
          </div>
        </div>

        {/* Tab nav */}
        <nav className="px-2 md:px-6 -mt-1 border-t border-white/5 overflow-x-auto">
          <div className="flex items-center gap-1 min-w-max">
            {nav.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={`hps-nav-${item.label.toLowerCase().split(" ")[0]}`}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                      isActive
                        ? "text-white border-blue-400"
                        : "text-white/55 border-transparent hover:text-white hover:border-white/20"
                    }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {item.label}
                </NavLink>
              );
            })}
          </div>
        </nav>
      </header>

      {/* KI-Disclaimer Banner (immer sichtbar) */}
      <div className="bg-amber-500/10 border-b border-amber-400/20 px-4 md:px-8 py-2 text-[11px] md:text-xs text-amber-200 flex items-center gap-2">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
        <span>
          Die KI unterstützt Bewertung und Dokumentation. Die finale fachliche Freigabe erfolgt durch eine verantwortliche Elektrofachkraft oder HPS.
        </span>
      </div>

      <main className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <Outlet />
      </main>

      <footer className="text-center text-[11px] text-white/40 py-6">
        Technische Plattform: <span className="text-white/70">TGAone</span> &nbsp;·&nbsp;
        Partnerbereich: <span className="text-white/70">HPS</span> &nbsp;·&nbsp; Pilotversion
      </footer>

      {/* Passwort-Wechsel Modal */}
      {showPwModal && (
        <PasswordChangeModal onDone={() => setShowPwModal(false)} />
      )}
    </div>
  );
}


function PasswordChangeModal({ onDone }) {
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (pw1.length < 8) return toast.error("Mindestens 8 Zeichen");
    if (pw1 !== pw2) return toast.error("Passwörter stimmen nicht überein");
    setBusy(true);
    try {
      const r = await authFetch(`${API}/hps/me/change-password`, {
        method: "POST",
        body: JSON.stringify({ new_password: pw1 }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Fehler");
      toast.success("Passwort geändert");
      // User-Objekt in localStorage updaten, damit Modal nicht wieder aufploppt
      try {
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        u.must_change_password = false;
        localStorage.setItem("user", JSON.stringify(u));
      } catch { /* noop */ }
      onDone();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }}>
      <div className="w-full max-w-md rounded-2xl border border-white/10 p-6"
           style={{ background: "linear-gradient(135deg,#122947,#0a1628)" }}>
        <h2 className="text-lg font-semibold text-white mb-1">Passwort festlegen</h2>
        <p className="text-xs text-white/60 mb-4">
          Beim ersten Login bitte ein eigenes sicheres Passwort vergeben (mind. 8 Zeichen).
        </p>
        <input type="password" value={pw1} onChange={(e) => setPw1(e.target.value)}
               placeholder="Neues Passwort" autoFocus
               data-testid="hps-pw-change-1"
               className="w-full rounded-lg bg-white/5 border border-white/15 px-3 py-2 text-white mb-3 focus:outline-none focus:border-blue-400" />
        <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
               placeholder="Wiederholen"
               data-testid="hps-pw-change-2"
               className="w-full rounded-lg bg-white/5 border border-white/15 px-3 py-2 text-white mb-4 focus:outline-none focus:border-blue-400" />
        <button onClick={submit} disabled={busy} data-testid="hps-pw-change-submit"
                className="w-full rounded-lg py-2.5 text-sm font-semibold text-white"
                style={{ background: "linear-gradient(135deg,#4a9eff,#2563eb)" }}>
          {busy ? "Speichern …" : "Passwort speichern"}
        </button>
      </div>
    </div>
  );
}
